import { useState, useRef, useEffect, type ReactNode } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from './ui/button';
import { cn } from '@/lib/utils';
import { ChevronDown, ChevronUp } from 'lucide-react';

type MarkdownDisplayProps = {
  content: string;
  variant?: 'rich' | 'plain' | 'simple';
  size?: 'sm' | 'base';
  expandable?: boolean;
  lines?: number;
  className?: string;
};

const ExternalLink: Components['a'] = ({ node, ...props }) => (
  <a
    {...props}
    target="_blank"
    rel="noopener noreferrer"
    className="text-primary underline underline-offset-2 hover:text-primary/80"
  />
);

const BoldParagraph = ({ children }: { children?: ReactNode }) => (
  <p>
    <strong>{children}</strong>
  </p>
);

const componentsByVariant: Record<
  NonNullable<MarkdownDisplayProps['variant']>,
  Components
> = {
  plain: {
    h1: ({ children }) => <span>{children}</span>,
    h2: ({ children }) => <span>{children}</span>,
    h3: ({ children }) => <span>{children}</span>,
    strong: ({ children }) => <>{children}</>,
    em: ({ children }) => <>{children}</>,
    a: ({ children }) => <>{children}</>,
    blockquote: ({ children }) => <span>{children}</span>,
    ul: ({ children }) => <>{children}</>,
    ol: ({ children }) => <>{children}</>,
    li: ({ children }) => <span>{children}</span>,
    code: ({ children }) => <>{children}</>,
    br: () => <> </>,
    hr: () => <> </>,
  },
  simple: {
    h1: BoldParagraph,
    h2: BoldParagraph,
    h3: BoldParagraph,
    a: ExternalLink,
  },
  rich: {
    a: ExternalLink,
  },
};

export default function MarkdownDisplay({
  content,
  variant = 'rich',
  size = 'base',
  expandable = false,
  lines = 5,
  className,
}: MarkdownDisplayProps) {
  if (!content) return null;

  const classes = cn(
    variant === 'plain'
      ? 'text-sm'
      : cn('prose dark:prose-invert max-w-none', size === 'sm' && 'prose-sm'),
    className
  );

  const markdown = (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={componentsByVariant[variant]}
    >
      {content}
    </ReactMarkdown>
  );

  if (!expandable) return <div className={classes}>{markdown}</div>;

  return (
    <Expandable lines={lines} className={classes}>
      {markdown}
    </Expandable>
  );
}

function Expandable({
  lines,
  className,
  children,
}: {
  lines: number;
  className?: string;
  children: ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || expanded) return;

    const check = () => setOverflowing(el.scrollHeight > el.clientHeight + 1);
    check();

    // Re-check when the width changes (window resize, dialog open, etc.)
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [expanded, children]);

  return (
    <div>
      <div
        ref={ref}
        className={cn(className, 'wrap-break-words', !expanded && 'overflow-hidden')}
        style={
          expanded
            ? undefined
            : {
                maxHeight: `${lines}lh`,
                ...(overflowing && {
                  maskImage:
                    'linear-gradient(to bottom, black calc(100% - 2.5rem), transparent)',
                }),
              }
        }
      >
        {children}
      </div>
      {(overflowing || expanded) && (
        <Button
          variant="link"
          size="sm"
          className={cn("h-auto p-0 text-xs cursor-pointer", expanded && 'mt-3')}
          onClick={() => setExpanded((e) => !e)}
        >
          {expanded ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
          {expanded ? 'Show less' : 'Show more'}
        </Button>
      )}
    </div>
  );
}
